import type { TFunction } from 'i18next';
import { formatClock } from '@/features/trips/format';

/**
 * SLICE D2 — the owner's `driver_unlink_requested` alert row. A driver cannot
 * unlink an owner-assigned vehicle himself; from the Raad Driver app he can only
 * REQUEST it, which lands here in the owner's alerts feed (PLAN §Driver endpoints
 * `POST /unlink-request`). The owner then ends the assignment with D1's control —
 * there is no new screen.
 *
 * 🔴 A TYPED MEMBER OF THE FEED UNION (slice D2, api `DriverUnlinkAlertZ`).
 * `FeedRow` narrows it with `isDriverUnlink` and hands it here, exactly like every
 * other typed row. It carries NO location (a request is not a positioned event), so
 * it is excluded from `PositionedFeedItem` and is a plain markable button, never a
 * map-select — the same discipline as the tracker-health and data-delayed rows.
 *
 * The prop interface is deliberately kept tolerant (base fields plus a defensive
 * `driverName`/`observedAt` read, like `GenericAlertRow`): the typed `DriverUnlinkAlert`
 * is structurally assignable to it, and a loose or older cached wire item still
 * renders rather than crashing the feed. If the shipped JSON ever disagrees with what
 * is read here, that is a seam break to report (types.ts §seam note).
 */
export interface DriverUnlinkWireAlert {
  id: string;
  vehicleId: string;
  read: boolean;
  type?: unknown;
  observedAt?: unknown;
  driverName?: unknown;
}

export function DriverUnlinkRow({
  item,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  item: DriverUnlinkWireAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: () => void;
  t: TFunction;
}) {
  const driverName =
    typeof item.driverName === 'string' && item.driverName.trim() !== ''
      ? item.driverName.trim()
      : t('alerts.driverUnlink.driverFallback');
  const vehicle = plate && plate.trim() !== '' ? plate : t('alerts.driverUnlink.vehicleFallback');
  const body = t('alerts.driverUnlink.body', { driver: driverName, vehicle });

  const observedMs = typeof item.observedAt === 'string' ? Date.parse(item.observedAt) : NaN;
  const observedTime = Number.isNaN(observedMs) ? null : formatClock(locale, observedMs, timeZone);
  const dotClass = item.read ? 'bg-transparent' : 'bg-raad-accent';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        data-testid="driver-unlink-row"
        data-type="driver_unlink_requested"
        data-alert-id={item.id}
        className={[
          'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-raad-text">{t('alerts.driverUnlink.title')}</span>
            {!item.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            <div className="mt-0.5 text-[12.5px] text-raad-muted">{body}</div>
            {observedTime ? (
              <div className="mt-1 text-[12px] text-raad-muted">
                {t('alerts.observedAt', { time: observedTime })}
              </div>
            ) : null}
          </div>
        </div>
      </button>
    </li>
  );
}
